import { useEffect, useState } from "react";
import { Button, Card, Checkbox, Cluster, FormField, Input, Select, Stack } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { Flash } from "./Flash";
import { api } from "./api";
import { notifySaved } from "./savedToast";
import { formatDateUtc, formatUtc, money } from "./format";

/** The date half of an ISO timestamp, for a date input. */
export function expiryInputValue(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : "";
}

/** A date input's value as the end of that day (UTC); blank means no end date. */
export function expiryPayload(value: string): string | null {
  return value ? `${value}T23:59:59Z` : null;
}

/** "Expires" date for an add-on card. Blank means the switch alone decides. */
export function ExpiryField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <FormField
      label="Expires"
      htmlFor={id}
      tooltip="The add-on switches off at the end of this day (UTC). Leave blank for no end date. A paid AI offer sets it."
    >
      <Input id={id} type="date" value={value} onChange={(event) => onChange(event.target.value)} />
    </FormField>
  );
}

/** "Expired on" or "Active until" line under an add-on card. */
export function ExpiryNote({ expiresAt, expired }: { expiresAt: string | null; expired: boolean }) {
  if (!expiresAt) return null;
  return (
    <p className="admin-muted">
      {expired ? `Expired on ${formatDateUtc(expiresAt)}. The customer no longer has it.` : `Active until ${formatDateUtc(expiresAt)}.`}
    </p>
  );
}

interface MailboxAiSettings {
  enabled: boolean;
  switched_on: boolean;
  expires_at: string | null;
  expired: boolean;
}

/** Mailbox AI add-on: AI sorting of the shared inbox. The mailbox itself is on the Enterprise plan. */
export function MailboxAiCard({ orgId }: { orgId: string }) {
  const [row, setRow] = useState<MailboxAiSettings | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [expires, setExpires] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function apply(next: MailboxAiSettings) {
    setRow(next);
    setEnabled(Boolean(next?.switched_on));
    setExpires(expiryInputValue(next?.expires_at));
  }

  useEffect(() => {
    api
      .get<MailboxAiSettings>(`/operator/orgs/${orgId}/mailbox-ai`)
      .then(apply)
      .catch((err) => setError(err instanceof Error ? err.message : "Mailbox AI settings could not be loaded."));
  }, [orgId]);

  async function save() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      apply(await api.put<MailboxAiSettings>(`/operator/orgs/${orgId}/mailbox-ai`, {
        enabled,
        expires_at: expiryPayload(expires),
      }));
      setNotice("Mailbox AI saved.");
      notifySaved("Mailbox AI saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mailbox AI settings could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <Stack gap={3}>
        <TipHeading
          label="Mailbox AI"
          tip="Paid add-on, outside every plan. Lets the shared mailbox (Enterprise) sort each email with AI. Needs Organization AI enabled too."
        />
        {error && <Flash tone="error">{error}</Flash>}
        {notice && <Flash tone="success">{notice}</Flash>}
        <Cluster gap={2} align="end">
          <Checkbox label="Enabled" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
          <ExpiryField id="mailbox-ai-expires" value={expires} onChange={setExpires} />
          <Button onClick={() => void save()} disabled={saving || !row}>
            {saving ? "Saving…" : "Save Mailbox AI"}
          </Button>
        </Cluster>
        {row ? <ExpiryNote expiresAt={row.expires_at} expired={row.expired} /> : null}
      </Stack>
    </Card>
  );
}

interface OfferLine {
  addon: "leadgen" | "loxley" | "mailbox_ai";
  label: string;
  monthly_cents: number;
  quantity: number;
  term_cents: number;
}

interface AiOffer {
  id: string;
  status: "draft" | "pushed" | "paid" | "cancelled";
  currency: string;
  term_months: number;
  lines: OfferLine[];
  subtotal_cents: number;
  tax_percent: number;
  tax_label: string | null;
  tax_cents: number;
  total_cents: number;
  note: string | null;
  created_at: string | null;
  pushed_at: string | null;
  paid_at: string | null;
  payment_method: string | null;
  pf_payment_id: string | null;
  expiries: Record<string, string>;
  last_error: string | null;
}

interface AiBillingView {
  addons: { addon: string; label: string; active: boolean; switched_on: boolean; expires_at: string | null }[];
  offers: AiOffer[];
  currencies: string[];
  has_saved_card: boolean;
}

const STATUS_LABEL: Record<AiOffer["status"], string> = {
  draft: "Draft",
  pushed: "With customer",
  paid: "Paid",
  cancelled: "Withdrawn",
};

function centsOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return Math.round(Number(trimmed) * 100);
}

function amountText(cents: number | undefined): string {
  return cents == null ? "" : (cents / 100).toFixed(2);
}

/**
 * AI billing: staff price LeadGen, Loxley seats and Mailbox AI for a term, then push the
 * offer. The customer pays it under Settings, Billing, and the payment sets each expiry.
 */
export function AiBillingCard({ orgId }: { orgId: string }) {
  const [view, setView] = useState<AiBillingView | null>(null);
  const [currency, setCurrency] = useState("USD");
  const [term, setTerm] = useState("12");
  const [leadgen, setLeadgen] = useState("");
  const [seatPrice, setSeatPrice] = useState("");
  const [seats, setSeats] = useState("");
  const [mailbox, setMailbox] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function apply(raw: Partial<AiBillingView> | null) {
    const next: AiBillingView = {
      addons: raw?.addons ?? [],
      offers: raw?.offers ?? [],
      currencies: raw?.currencies?.length ? raw.currencies : ["USD"],
      has_saved_card: Boolean(raw?.has_saved_card),
    };
    setView(next);
    const open = next.offers.find((offer) => offer.status === "draft" || offer.status === "pushed");
    if (!open) return;
    const line = (addon: OfferLine["addon"]) => open.lines.find((item) => item.addon === addon);
    setCurrency(open.currency);
    setTerm(String(open.term_months));
    setLeadgen(amountText(line("leadgen")?.monthly_cents));
    setSeatPrice(amountText(line("loxley")?.monthly_cents));
    setSeats(line("loxley") ? String(line("loxley")!.quantity) : "");
    setMailbox(amountText(line("mailbox_ai")?.monthly_cents));
    setNote(open.note ?? "");
  }

  useEffect(() => {
    api
      .get<AiBillingView>(`/operator/orgs/${orgId}/ai-billing`)
      .then(apply)
      .catch((err) => setError(err instanceof Error ? err.message : "AI billing could not be loaded."));
  }, [orgId]);

  async function act(work: () => Promise<AiBillingView>, done: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      apply(await work());
      setNotice(done);
      notifySaved(done);
    } catch (err) {
      setError(err instanceof Error ? err.message : "AI billing could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  function saveDraft() {
    const seatCents = centsOrNull(seatPrice);
    return act(
      () =>
        api.put<AiBillingView>(`/operator/orgs/${orgId}/ai-billing/draft`, {
          currency,
          term_months: Number(term),
          leadgen_cents: centsOrNull(leadgen),
          loxley_seat_cents: seatCents,
          loxley_seats: seatCents == null ? null : Math.round(Number(seats) || 0),
          mailbox_cents: centsOrNull(mailbox),
          note: note.trim() || null,
        }),
      "Offer saved as a draft. Push it when it is ready.",
    );
  }

  const open = view?.offers.find((offer) => offer.status === "draft" || offer.status === "pushed") ?? null;
  const pushed = open?.status === "pushed";
  const history = view?.offers.filter((offer) => offer !== open) ?? [];

  return (
    <Card>
      <Stack gap={3}>
        <TipHeading
          label="AI billing"
          tip={
            "Price LeadGen, Loxley seats and Mailbox AI per month for a 1 or 12 month term, save the draft, then "
            + "push it. Org admins get a notification and pay under Settings, Billing: the saved card is charged, or "
            + "PayFast takes a one-off payment. Paying switches each add-on on and sets its expiry. Nothing renews by "
            + "itself. For a free trial, use the add-on's switch and expiry instead, and lower the Organization AI "
            + "monthly budget (for example to 2 USD)."
          }
        />
        {error && <Flash tone="error">{error}</Flash>}
        {notice && <Flash tone="success">{notice}</Flash>}
        {view ? (
          <p className="admin-muted">
            {view.addons
              .map((addon) => `${addon.label}: ${addon.active ? (addon.expires_at ? `until ${formatDateUtc(addon.expires_at)}` : "on") : "off"}`)
              .join(" · ")}
            {view.has_saved_card ? " · Saved card on file." : " · No saved card: the customer pays on PayFast."}
          </p>
        ) : null}
        <Cluster gap={2} align="end">
          <FormField label="Currency" htmlFor="ai-offer-currency">
            <Select id="ai-offer-currency" value={currency} disabled={pushed}
              onChange={(event) => setCurrency(event.target.value)}>
              {(view?.currencies ?? ["USD"]).map((code) => <option key={code} value={code}>{code}</option>)}
            </Select>
          </FormField>
          <FormField label="Term" htmlFor="ai-offer-term">
            <Select id="ai-offer-term" value={term} disabled={pushed} onChange={(event) => setTerm(event.target.value)}>
              <option value="1">1 month</option>
              <option value="12">12 months</option>
            </Select>
          </FormField>
          <FormField label="LeadGen per month" htmlFor="ai-offer-leadgen" tooltip="Blank leaves LeadGen out of the offer.">
            <Input id="ai-offer-leadgen" type="number" min={0} step="0.01" value={leadgen} disabled={pushed}
              onChange={(event) => setLeadgen(event.target.value)} />
          </FormField>
          <FormField label="Loxley per seat per month" htmlFor="ai-offer-seat-price"
            tooltip="Blank leaves Loxley out of the offer.">
            <Input id="ai-offer-seat-price" type="number" min={0} step="0.01" value={seatPrice} disabled={pushed}
              onChange={(event) => setSeatPrice(event.target.value)} />
          </FormField>
          <FormField label="Loxley seats" htmlFor="ai-offer-seats">
            <Input id="ai-offer-seats" type="number" min={1} max={10000} value={seats} disabled={pushed}
              onChange={(event) => setSeats(event.target.value)} />
          </FormField>
          <FormField label="Mailbox AI per month" htmlFor="ai-offer-mailbox"
            tooltip="Blank leaves Mailbox AI out of the offer. The shared mailbox needs the Enterprise plan.">
            <Input id="ai-offer-mailbox" type="number" min={0} step="0.01" value={mailbox} disabled={pushed}
              onChange={(event) => setMailbox(event.target.value)} />
          </FormField>
        </Cluster>
        <FormField label="Note to the customer" htmlFor="ai-offer-note">
          <Input id="ai-offer-note" value={note} maxLength={500} disabled={pushed}
            onChange={(event) => setNote(event.target.value)} />
        </FormField>
        {open ? (
          <p className="admin-muted" data-testid="ai-offer-total">
            {STATUS_LABEL[open.status]}: {money(open.subtotal_cents, open.currency)}
            {open.tax_cents ? ` + ${open.tax_label ?? "tax"} ${money(open.tax_cents, open.currency)}` : ""}
            {" = "}{money(open.total_cents, open.currency)} for {open.term_months} month{open.term_months === 1 ? "" : "s"}.
            {open.pushed_at ? ` Pushed ${formatUtc(open.pushed_at)}.` : ""}
            {open.last_error ? ` Last payment attempt: ${open.last_error}` : ""}
          </p>
        ) : null}
        <Cluster gap={2}>
          <Button onClick={() => void saveDraft()} disabled={busy || !view || pushed}>Save draft</Button>
          <Button
            variant="primary"
            disabled={busy || !open || pushed}
            onClick={() => void act(
              () => api.post<AiBillingView>(`/operator/orgs/${orgId}/ai-billing/offers/${open!.id}/push`, {}),
              "Offer pushed. The customer's org admins have been notified.",
            )}
          >
            Push to customer
          </Button>
          <Button
            variant="secondary"
            disabled={busy || !open}
            onClick={() => void act(
              () => api.post<AiBillingView>(`/operator/orgs/${orgId}/ai-billing/offers/${open!.id}/withdraw`, {}),
              "Offer withdrawn.",
            )}
          >
            {pushed ? "Withdraw offer" : "Discard draft"}
          </Button>
        </Cluster>
        {history.length ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Services</th>
                <th>Total</th>
                <th>Status</th>
                <th>Paid</th>
                <th>Expiry set</th>
              </tr>
            </thead>
            <tbody>
              {history.map((offer) => (
                <tr key={offer.id}>
                  <td>{formatUtc(offer.created_at)}</td>
                  <td>{offer.lines.map((line) => line.label).join(", ")}</td>
                  <td>{money(offer.total_cents, offer.currency)} / {offer.term_months} mo</td>
                  <td>{STATUS_LABEL[offer.status]}</td>
                  <td>
                    {offer.paid_at ? `${formatUtc(offer.paid_at)} (${offer.payment_method ?? "—"})` : "—"}
                    {offer.pf_payment_id ? ` · ${offer.pf_payment_id}` : ""}
                  </td>
                  <td>
                    {Object.keys(offer.expiries ?? {}).length
                      ? Object.values(offer.expiries).map((iso) => formatDateUtc(iso)).filter((v, i, all) => all.indexOf(v) === i).join(", ")
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Stack>
    </Card>
  );
}
